import React from "react";
import styled from "styled-components";
import { FormattedMessage } from "react-intl";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import TimerIcon from "@mui/icons-material/Timer";
import LocationSearchingIcon from "@mui/icons-material/LocationSearching";
import { ReactComponent as PickupMarker } from "../../assets/icons/pickup.svg";
import { ReactComponent as EndPoint } from "../../assets/icons/end-point.svg";
import { ReactComponent as WaitingPointMarker } from "../../assets/icons/WaitingPointMarker.svg";
import { ReactComponent as LowBatteryMarker } from "../../assets/icons/LowBatteryMarker.svg";
import { ReactComponent as DeadBatteryMarker } from "../../assets/icons/DeadBatteryMarker.svg";
import DropoffMarker from "./DropoffMarker";
import { dateToFormattedTime, dateToNumberAndMonth } from "../../utils/dateAndTime";

const Container = styled.div`
    background-color: #fff;
    border-radius: 8px;
    padding: 12px 30px;
    box-shadow: 0px 20px 25px -5px rgba(0, 0, 0, 0.1), 0px 10px 10px -5px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-width: 320px;
`;

const Heading = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const HeadingDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;

    h4 {
        color: #000405;
        font-size: 14px;
        font-weight: 700;
    }

    p {
        color: #40444d;
        font-weight: 400;
        font-size: 12px;
    }
`;

const Details = styled.div`
    display: flex;
    align-items: start;
    gap: 6px;
    font-weight: 400;
    font-size: 14px;
    color: #40444d;
    flex-wrap: wrap;

    span {
        color: #000405;
        font-weight: 500;
        font-size: 16px;
    }

    svg {
        width: 16px;
        height: 16px;
        color: #40444d;
    }
`;

const LocationDetails = styled.div`
    display: flex;
    align-items: start;
    gap: 6px;
    font-weight: 400;
    font-size: 14px;
    color: #40444d;

    svg {
        width: 16px;
        height: 16px;
        color: #40444d;
    }
`;

const DetailsData = styled.div`
    flex-basis: 100%;
    margin-left: 23px;
`;

const LocationContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background: #f5f8fa;
    padding: 10px 20px;

    svg {
        color: #00508a;
        width: 16px;
        height: 16px;
        cursor: pointer;
    }

    p {
        color: #00508a;
        font-weight: 400;
        font-size: 14px;
        flex-grow: 1;
    }
`;

const PointDetails = ({ pointType, point }) => {
    const locale = localStorage.getItem("locale");
    const waitingTimeHours = Math.floor((point?.waitingTime ?? 0) / 60);
    const waitingTimeMinutes = (point?.waitingTime ?? 0) % 60;

    const onCopyLatLng = () => {
        if (typeof navigator !== "undefined") {
            navigator.clipboard.writeText(`${point?.lat}, ${point?.lng}`);
        }
    };

    const renderMrkerIcon = () => {
        switch (pointType) {
            case "pickup":
                return <PickupMarker />;
            case "dropoff":
                return <DropoffMarker index={point?.index} />;
            case "endPoint":
                return <EndPoint />;
            case "waitingPoint":
                return <WaitingPointMarker />;
            case "lowBatteryPoint":
                return <LowBatteryMarker />;
            case "deadBatteryPoint":
                return <DeadBatteryMarker />;
            default:
                return null;
        }
    };

    const getPointName = () => {
        if (pointType === "waitingPoint") {
            return <FormattedMessage id="google.map.waiting.point" />;
        }
        if (pointType === "lowBatteryPoint") {
            return <FormattedMessage id="google.map.low.battery.point" />;
        }
        if (pointType === "deadBatteryPoint") {
            return <FormattedMessage id="google.map.dead.battery.point" />;
        }
        const name =
            locale === "en" && pointType !== "waitingPoint"
                ? point?.nameEn || point?.name
                : point?.nameAr || point?.name;
        return name;
    };

    const getPointTypeMessage = (pointDetails) => {
        switch (pointType) {
            case "pickup":
                return <FormattedMessage id="google.map.pickup.point" />;
            case "dropoff":
                return <FormattedMessage id="google.map.dropoff.point" />;
            case "endPoint":
                return <FormattedMessage id="google.map.dropoff.end.point" />;
            case "lowBatteryPoint":
                return `~${pointDetails?.batteryLevel}%`;
            default:
                return null;
        }
    };

    return (
        <Container dir={locale === `"en"` || locale === "en" ? "ltr" : "rtl"}>
            <Heading>
                {renderMrkerIcon()}
                <HeadingDetails>
                    <h4>{getPointName()}</h4>
                    <p>{getPointTypeMessage(point)}</p>
                </HeadingDetails>
            </Heading>
            {point?.address && (
                <LocationDetails>
                    <LocationOnIcon />
                    {point?.address}
                </LocationDetails>
            )}
            <Details>
                <AccessTimeFilledIcon />
                <FormattedMessage id="google.map.occured.at" />
                <DetailsData>
                    <span>{dateToFormattedTime(point?.loggedAt)}</span> |{" "}
                    <span>{dateToNumberAndMonth(point?.loggedAt)}</span>
                </DetailsData>
            </Details>
            {point?.waitingTime && (
                <Details>
                    <TimerIcon />
                    <FormattedMessage id="google.map.duration" />
                    <DetailsData>
                        <span>{waitingTimeHours}</span>h <span>{waitingTimeMinutes}</span>min
                    </DetailsData>
                </Details>
            )}
            <LocationContainer>
                <LocationSearchingIcon />
                <p>{`${point?.lat}, ${point?.lng}`}</p>
                <ContentCopyIcon onClick={onCopyLatLng} />
            </LocationContainer>
        </Container>
    );
};

export default PointDetails;
